import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTokenValidation } from '@/hooks/useTokenValidation';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useProfile } from '@/contexts/UserProfileContext';
import type { SubscriptionTier, SubscriptionProduct } from '@/config/subscriptionPlans';
import type { SubscriptionStatus } from '@/config/productConfig';

export interface ProductEntitlement {
  product: SubscriptionProduct;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  monthlyUsage: number;
  currentPeriodEnd: string | null;
  isActive: boolean;
  isUnlimited: boolean;
}

// Products that can have individual entitlements (bundle gives access to both)
type EntitlementProduct = 'career_path' | 'career_studio';

interface UseProductEntitlementsResult {
  entitlements: Record<EntitlementProduct, ProductEntitlement>;
  isLoading: boolean;
  error: string | null;
  hasProduct: (product: SubscriptionProduct) => boolean;
  canAccessProduct: (product: SubscriptionProduct) => boolean;
  getProductTier: (product: SubscriptionProduct) => SubscriptionTier;
  hasBundle: boolean;
  refetch: () => Promise<void>;
  incrementUsage: (product: EntitlementProduct) => Promise<{ canAccess: boolean; currentUsage: number; maxUsage: number }>;
}

const defaultEntitlement: ProductEntitlement = {
  product: 'career_path',
  tier: 'free',
  status: 'free',
  monthlyUsage: 0,
  currentPeriodEnd: null,
  isActive: false,
  isUnlimited: false,
};

export const useProductEntitlements = (): UseProductEntitlementsResult => {
  const { result } = useTokenValidation();
  const { profile } = useProfile();
  const [entitlements, setEntitlements] = useState<Record<EntitlementProduct, ProductEntitlement>>({
    career_path: { ...defaultEntitlement, product: 'career_path' },
    career_studio: { ...defaultEntitlement, product: 'career_studio' },
  });
  const [hasBundle, setHasBundle] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const profileId = profile?.id;
  const { isAdmin: roleIsAdmin, isSuperAdmin } = useUserRoles(profileId);
  const isAdmin = result?.isAdmin || roleIsAdmin || isSuperAdmin;

  const fetchEntitlements = useCallback(async () => {
    if (!profileId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await (supabase as any)
        .from('user_product_entitlements')
        .select('*')
        .eq('user_id', profileId)
        .limit(50);

      if (fetchError) {
        console.error('[useProductEntitlements] Error fetching entitlements:', fetchError);
        setError(fetchError.message);
        return;
      }

      const newEntitlements: Record<EntitlementProduct, ProductEntitlement> = {
        career_path: { ...defaultEntitlement, product: 'career_path' },
        career_studio: { ...defaultEntitlement, product: 'career_studio' },
      };

      let userHasBundle = false;

      if (data) {
        data.forEach((row: any) => {
          const product = row.product as SubscriptionProduct;
          const tier = row.subscription_tier as SubscriptionTier;
          const isUnlimited = tier !== 'free';
          
          // Check if user has bundle subscription
          if (product === 'bundle') {
            userHasBundle = true;
            // Bundle gives access to both products
            newEntitlements.career_path = {
              product: 'career_path',
              tier,
              status: row.subscription_status as SubscriptionStatus,
              monthlyUsage: row.monthly_usage_count || 0,
              currentPeriodEnd: row.current_period_end,
              isActive: tier !== 'free' && row.subscription_status === 'active',
              isUnlimited,
            };
            newEntitlements.career_studio = {
              product: 'career_studio',
              tier,
              status: row.subscription_status as SubscriptionStatus,
              monthlyUsage: row.monthly_usage_count || 0,
              currentPeriodEnd: row.current_period_end,
              isActive: tier !== 'free' && row.subscription_status === 'active',
              isUnlimited,
            };
          } else if (product === 'career_path' || product === 'career_studio') {
            newEntitlements[product] = {
              product,
              tier,
              status: row.subscription_status as SubscriptionStatus,
              monthlyUsage: row.monthly_usage_count || 0,
              currentPeriodEnd: row.current_period_end,
              isActive: tier !== 'free' && row.subscription_status === 'active',
              isUnlimited,
            };
          }
        });
      }

      // If user is admin, give them unlimited access to all products
      if (isAdmin) {
        Object.keys(newEntitlements).forEach((key) => {
          const product = key as EntitlementProduct;
          newEntitlements[product].isUnlimited = true;
          newEntitlements[product].isActive = true;
          newEntitlements[product].tier = 'elite';
        });
      }

      setHasBundle(userHasBundle);
      setEntitlements(newEntitlements);
    } catch (err) {
      console.error('[useProductEntitlements] Exception:', err);
      setError('Failed to fetch entitlements');
    } finally {
      setIsLoading(false);
    }
  }, [profileId, isAdmin]);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  const hasProduct = useCallback((product: SubscriptionProduct): boolean => {
    if (isAdmin) return true;
    if (product === 'bundle') return hasBundle;
    if (hasBundle) return true; // Bundle gives access to all products
    
    const entitlementProduct = product as EntitlementProduct;
    return entitlements[entitlementProduct]?.isActive || false;
  }, [entitlements, isAdmin, hasBundle]);

  const canAccessProduct = useCallback((product: SubscriptionProduct): boolean => {
    if (isAdmin) return true;
    if (hasBundle) return true; // Bundle gives access to all products
    
    if (product === 'bundle') return hasBundle;
    
    const entitlementProduct = product as EntitlementProduct;
    const entitlement = entitlements[entitlementProduct];
    if (!entitlement) return false;
    
    // Pro/Elite users have unlimited access
    if (entitlement.isUnlimited) return true;
    
    // Free/Lite users have limited access (checked via usage)
    return true; // Usage is tracked separately
  }, [entitlements, isAdmin, hasBundle]);

  const getProductTier = useCallback((product: SubscriptionProduct): SubscriptionTier => {
    if (isAdmin) return 'elite';
    if (product === 'bundle') return hasBundle ? 'pro' : 'free';
    
    const entitlementProduct = product as EntitlementProduct;
    return entitlements[entitlementProduct]?.tier || 'free';
  }, [entitlements, isAdmin, hasBundle]);

  const incrementUsage = useCallback(async (product: EntitlementProduct): Promise<{ canAccess: boolean; currentUsage: number; maxUsage: number }> => {
    if (!profileId) {
      return { canAccess: false, currentUsage: 0, maxUsage: 2 };
    }

    // Admin users bypass usage limits
    if (isAdmin) {
      return { canAccess: true, currentUsage: 0, maxUsage: 999999 };
    }

    try {
      const { data, error: rpcError } = await (supabase as any).rpc('increment_product_usage', {
        p_user_profile_id: profileId,
        p_product: product,
      });

      if (rpcError) {
        console.error('[useProductEntitlements] Error incrementing usage:', rpcError);
        return { canAccess: false, currentUsage: 0, maxUsage: 2 };
      }

      const usageResult = data?.[0] || { can_access: false, current_usage: 0, max_usage: 2 };
      
      // Refresh entitlements after usage update
      await fetchEntitlements();

      return {
        canAccess: usageResult.can_access,
        currentUsage: usageResult.current_usage,
        maxUsage: usageResult.max_usage,
      };
    } catch (err) {
      console.error('[useProductEntitlements] Exception incrementing usage:', err);
      return { canAccess: false, currentUsage: 0, maxUsage: 2 };
    }
  }, [profileId, isAdmin, fetchEntitlements]);

  return {
    entitlements,
    isLoading,
    error,
    hasProduct,
    canAccessProduct,
    getProductTier,
    hasBundle,
    refetch: fetchEntitlements,
    incrementUsage,
  };
};